import React, { useCallback, useState } from "react";
import { CanvasArtwork } from "../data/canvasArtworks";
import { useCanvasArtwork } from "../hooks/useTranslatedArtwork";
import { ArtworkLightboxShell } from "./ArtworkLightboxShell";
import { CanvasLightboxContent } from "./CanvasArtworkCards";

export function CanvasLightbox({
  work,
  onClose,
  onNavigate,
  showNavigation = true,
  exploreGalleryHref,
}: {
  work: CanvasArtwork;
  onClose: () => void;
  onNavigate?: (direction: -1 | 1) => void;
  showNavigation?: boolean;
  exploreGalleryHref?: string;
}) {
  const translated = useCanvasArtwork(work);
  const [imageIndex, setImageIndex] = useState(0);
  const images = translated.galleryImages;
  const count = images.length;

  const goToImage = useCallback(
    (index: number) => {
      setImageIndex(((index % count) + count) % count);
    },
    [count],
  );

  const navigateImage = useCallback(
    (direction: -1 | 1) => {
      goToImage(imageIndex + direction);
    },
    [goToImage, imageIndex],
  );

  const activeImage = images[imageIndex] ?? images[0];

  return (
    <ArtworkLightboxShell
      ariaLabel={work.id}
      imageUrl={activeImage?.url ?? work.imageUrl}
      imageAlt={activeImage?.alt ?? work.id}
      onClose={onClose}
      onNavigate={onNavigate}
      showNavigation={showNavigation}
      imageIndex={imageIndex}
      imageCount={count}
      onImageNavigate={count > 1 ? navigateImage : undefined}
      imageFooter={
        count > 1 ? (
          <div className="flex gap-2 overflow-x-auto border-t border-canvas-200/70 bg-canvas-50/60 p-3 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden">
            {images.map((image, index) => (
              <button
                key={`${image.url}-${index}`}
                type="button"
                onClick={() => goToImage(index)}
                className={`shrink-0 overflow-hidden rounded-lg border-0 p-0 ring-2 transition ${
                  index === imageIndex
                    ? "ring-hungru-gold/50"
                    : "ring-transparent opacity-70 hover:opacity-100"
                }`}
              >
                <img
                  src={image.url}
                  alt={image.alt}
                  className="h-14 w-14 object-cover"
                />
              </button>
            ))}
          </div>
        ) : undefined
      }
    >
      <CanvasLightboxContent work={work} exploreGalleryHref={exploreGalleryHref} />
    </ArtworkLightboxShell>
  );
}
